import { useContext } from "react";
import { useTranslation } from "next-i18next";
import { useTradeInfo } from "@/redux/selectors/tradeInfoSelector";
import { daysOfWeek, DaysOfWeek, useMarketSchedule } from "@/hooks/trade/schedule";
import { useActiveSymbolDynamicFields } from "@/hooks/symbols";
import { IContent as IInfoListContent } from "@/components/Core/InfoList/InfoList";
import { padDate } from "@/utils/format";
import { TradeOrderStateContext } from "@/contexts/TradeOrder";

import { formatWeeklySchedule, mergeWeeklySchedule } from '@/utils/schedule'

interface IInfoPageData {
    symbolData: IInfoListContent[];
    tradingHoursData: IInfoListContent[];
    pendingOrdersData: IInfoListContent[];
}


export const useInfoPageData = (): IInfoPageData => {
    const { t } = useTranslation("common");
    const context = useContext(TradeOrderStateContext);
    const { symbolsInfoById, activeSymbolId, internalSymbolId } = useTradeInfo();
    const symbolInfo = symbolsInfoById[activeSymbolId];

    const urlParams = new URLSearchParams(window.location.search);
    const isMTR = urlParams.get("mtr") === "1";

    const { lotSize } = context || { lotSize: symbolInfo?.group?.lotMin || 1 };
    const { schedule } = useMarketSchedule(activeSymbolId);
    //console.log('schedule', schedule)
    const {
        lotDisplayValue,
        isAccountCurrencySymbol,
        marginDisplayValueInSymbolCurrency,
        leverageDisplayValue,
        requiredBuyMarginDisplayValue,
        requiredSellMarginDisplayValue,
        swapBuyDisplayValue,
        swapSellDisplayValue,
    } = useActiveSymbolDynamicFields({ lotSize });

    const quoteAsset = internalSymbolId ? internalSymbolId : symbolInfo?.ex?.displayName || symbolInfo?.id;


    const symbolData: IInfoListContent[] = [
        {
            label: t("base_asset"),
            value: !isMTR ? t(symbolInfo?.currency) : symbolInfo?.ex?.displayName,
        },
        {
            label: !isMTR ? t("quote_asset") : t("quote_currency"),
            value: !isMTR ? quoteAsset : symbolInfo?.ex?.quoteCurrency,

        },
        {
            label: t("leverage"),
            value: `${leverageDisplayValue}`,
        },
        {
            label: t("spread"),
            value: t(symbolInfo?.ex?.spreadType?.toLowerCase()),
        },
        {
            label: t("digits"),
            value: String(symbolInfo?.digits),
        },
        {
            label: t("lot_value"),
            value: `${lotDisplayValue}`,
        },
        {
            label: t("min_max_volume"),
            value: `${symbolInfo?.group?.lotMin} - ${symbolInfo?.group?.lotMax}`,
        },
        {
            label: t("volume_step"),
            value: `${symbolInfo?.group?.lotStep}`,
        },
        {
            label: t("required_margin_buy", { lots: lotSize }),
            value: isAccountCurrencySymbol
                ? requiredBuyMarginDisplayValue
                : `${marginDisplayValueInSymbolCurrency} (${requiredBuyMarginDisplayValue})`,
            valueType: "price",
        },
        {
            label: t("required_margin_sell", { lots: lotSize }),
            value: isAccountCurrencySymbol
                ? requiredSellMarginDisplayValue
                : `${marginDisplayValueInSymbolCurrency} (${requiredSellMarginDisplayValue})`,
            valueType: "price",
        },
        {
            label: t("swap_buy"),
            value: swapBuyDisplayValue,
            valueType: "price",
        },
        {
            label: t("swap_sell"),
            value: swapSellDisplayValue,
            valueType: "price",
        },
        {
            label: "",
            value: isMTR ? t("swaps_are_charged_everyday") : t("on_wednesday_3_swaps_are_changed"),
        },
    ];

    if (symbolInfo?.ex?.rolloverDate) {
        const rolloverDate = new Date(symbolInfo?.ex?.rolloverDate);
        const rolloverDateInfo = {
            label: t("rolloverDate"),
            value: `${padDate(rolloverDate.getDate())}.${padDate(
                rolloverDate.getMonth() + 1
            )}.${rolloverDate.getFullYear()}`,
        };

        // after Leverage
        symbolData.splice(3, 0, rolloverDateInfo);
    }

    const dayLabels: Record<DaysOfWeek, string> = {
        mon: "Mon",
        tue: "Tue",
        wed: "Wed",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
        sun: "Sun",
    };

    const tradingHoursData: IInfoListContent[] = daysOfWeek.map(day => {
        if (schedule === "OPEN247") {
            return {
                label: t(`daysOfWeek.${dayLabels[day]}`),
                value: "00:00:00-24:00:00",
            };
        }

        const daySchedule = schedule[day];

        if (!daySchedule || !daySchedule.length) {
            return {
                label: t(`daysOfWeek.${dayLabels[day]}`),
                value: t("closed"),
            };
        }

        if (!isMTR) {
            const scheduleLines = daySchedule.map(slot => {
                const [from, to] = slot;
                const hhFrom = padDate(from.getHours());
                const mmFrom = padDate(from.getMinutes());

                const hhTo = padDate(to.getHours());
                const mmTo = padDate(to.getMinutes());

                if (hhTo === "23" && mmTo === "59") {
                    return `${hhFrom}:${mmFrom}–24:00`;
                }

                return `${hhFrom}:${mmFrom}–${hhTo}:${mmTo}`;
            });

            return {
                label: t(`daysOfWeek.${dayLabels[day]}`),
                value: scheduleLines.join(", "),
            };
        } else {
            const scheduleLinesFormatted = formatWeeklySchedule(schedule);

            const result = mergeWeeklySchedule(scheduleLinesFormatted)

            return {
                label: t(`daysOfWeek.${dayLabels[day]}`),
                value: result[day].join(", "),
            };
        }
    });

    const pendingOrdersData: IInfoListContent[] = [
        {
            label: t("pendingTrades"),
            value: t("pending_order_description"),
        },
        {
            label: t("actionsId.buy_stop"),
            value: t("buy_stop_description"),
        },
        {
            label: t("actionsId.sell_stop"),
            value: t("sell_stop_description"),
        },
        {
            label: t("actionsId.buy_limit"),
            value: t("buy_limit_description"),
        },
        {
            label: t("actionsId.sell_limit"),
            value: t("sell_limit_description"),
        },
    ];

    return {
        symbolData,
        tradingHoursData,
        pendingOrdersData,
    };
};
